import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Modal } from "../ui/Modal";
import { agentsApi, type AgentExportRow } from "../../api/agentsApi";
import { downloadExport, type ExportFormat } from "../../lib/dataSheetTransfer";
import { DESTINATION_COLUMNS, DESTINATION_COLUMN_GROUPS, exportValue, type DestinationColumnKey } from "../../lib/destinationTransfer";

/**
 * Export dialog for destinations: pick the columns (only the fields the
 * detail screen edits, plus reference and PBX login) and the file format.
 * The rows come from one export call so large customers don't need a
 * detail request per destination.
 */
export function DestinationExportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [format, setFormat] = useState<ExportFormat>("xlsx");
  const [selected, setSelected] = useState<Set<DestinationColumnKey>>(() => new Set(DESTINATION_COLUMNS.map((c) => c.key)));
  const [rows, setRows] = useState<AgentExportRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setRows(null);
    setError(null);
    agentsApi
      .exportRows()
      .then((r) => {
        if (!cancelled) setRows(r);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load destinations");
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const toggle = (key: DestinationColumnKey) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const columns = DESTINATION_COLUMNS.filter((c) => selected.has(c.key));

  const run = () => {
    if (!rows) return;
    const headers = columns.map((c) => c.label);
    const data = rows.map((r) => columns.map((c) => exportValue(r, c.key)));
    downloadExport(format, "destinations", headers, data);
    onClose();
  };

  const count = rows?.length ?? 0;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export destinations"
      maxWidth={640}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={run}
            disabled={!rows || count === 0 || columns.length === 0}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Download size={16} /> Export {count} {count === 1 ? "destination" : "destinations"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        {error && (
          <div style={{ color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>{error}</div>
        )}
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>Format</span>
          <div style={{ display: "flex", gap: 16 }}>
            {(["xlsx", "csv"] as ExportFormat[]).map((f) => (
              <label key={f} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <input type="radio" name="dest-export-format" checked={format === f} onChange={() => setFormat(f)} />
                {f === "xlsx" ? "Excel (.xlsx)" : "CSV (.csv)"}
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: "0.9rem", fontWeight: 700 }}>Columns</span>
            <span style={{ display: "inline-flex", gap: 10, fontSize: "0.85rem" }}>
              <button type="button" className="btn btn-secondary" style={{ padding: "4px 10px" }} onClick={() => setSelected(new Set(DESTINATION_COLUMNS.map((c) => c.key)))}>
                All
              </button>
              <button type="button" className="btn btn-secondary" style={{ padding: "4px 10px" }} onClick={() => setSelected(new Set())}>
                None
              </button>
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px 16px", alignItems: "start" }}>
            {DESTINATION_COLUMN_GROUPS.map((g) => (
              <div key={g} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--portal-muted)" }}>{g}</span>
                {DESTINATION_COLUMNS.filter((c) => c.group === g).map((c) => (
                  <label key={c.key} style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: "0.9rem" }}>
                    <input type="checkbox" checked={selected.has(c.key)} onChange={() => toggle(c.key)} />
                    <span>
                      {c.label}
                      {!c.importable && <span style={{ color: "var(--portal-muted)", fontSize: "0.75rem" }}> (export only)</span>}
                    </span>
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
        <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--portal-muted)" }}>
          Login passwords are never exported. PBX username and password are included so the file can be re-imported
          with the same PBX logins.
        </p>
      </div>
    </Modal>
  );
}
